/**
 * useMermaidAsync — render the placeholders emitted by
 * `markdownConfig.ts` for diagram types that beautiful-mermaid v1.1.3
 * doesn't handle (erDiagram, sequenceDiagram, classDiagram, gantt, …).
 *
 * Scans the supplied root for `.mermaid-async[data-mermaid-source]`
 * nodes and replaces their content with an SVG produced by the official
 * `mermaid` package. Re-runs whenever the root or its innerHTML changes
 * (we observe via MutationObserver) so newly rendered markdown (e.g.
 * after a CollabReadView poll) also gets enhanced.
 *
 * Nur noch für Arten, die beautiful-mermaid nicht kennt (gantt, pie,
 * mindmap, timeline, journey …), im einfachen Stil wie im Portal (Post #5447).
 * `suppressErrorRendering`: Ohne den Schalter hängt mermaid bei einem
 * Syntaxfehler sein Fehlerbild („Syntax error in text“) an <body>, es lag dann
 * über jeder Ansicht. Der Fehler kommt als Ausnahme zurück, der Block zeigt
 * den Quelltext. Die Messknoten, die mermaid an <body> hängt, werden auch im
 * Fehlerfall entfernt.
 */

import { useEffect } from 'react'
import mermaid from 'mermaid'

let initialized = false
function ensureInitialized() {
  if (initialized) return
  mermaid.initialize({
    startOnLoad: false,
    theme: 'neutral',
    securityLevel: 'strict',
    fontFamily: 'inherit',
    suppressErrorRendering: true,
  })
  initialized = true
}

/**
 * Attach to a DOM root once it mounts. The hook is idempotent — already-
 * rendered placeholders are skipped via `data-mermaid-rendered`.
 */
export function useMermaidAsync(root: HTMLElement | null): void {
  useEffect(() => {
    if (!root) return
    ensureInitialized()

    let cancelled = false
    let counter = 0
    const renderAll = async () => {
      if (cancelled) return
      const placeholders = root.querySelectorAll<HTMLElement>(
        '.mermaid-async:not([data-mermaid-rendered])',
      )
      for (const el of Array.from(placeholders)) {
        const source = el.getAttribute('data-mermaid-source') || ''
        if (!source.trim()) {
          el.setAttribute('data-mermaid-rendered', 'empty')
          continue
        }
        const id = `mermaid-async-${Date.now()}-${counter++}`
        let svg = ''
        let fehler: unknown = null
        try {
          svg = (await mermaid.render(id, source)).svg
        } catch (e) {
          fehler = e
        } finally {
          // Messknoten, die mermaid an <body> hängt, entfernen, BEVOR das
          // Ergebnis eingesetzt wird: das fertige SVG trägt dieselbe Kennung
          // und würde sonst gleich mit entfernt.
          document.getElementById(id)?.remove()
          document.getElementById(`d${id}`)?.remove()
        }
        if (cancelled) return
        if (svg) {
          el.innerHTML = svg
          el.setAttribute('data-mermaid-rendered', 'true')
        } else {
          el.innerHTML = `<pre class="mermaid-error"><code>${source
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')}</code></pre>`
          el.setAttribute('data-mermaid-rendered', 'error')
          // Still: Der Beitrag bleibt lesbar, der Quelltext steht da.
          console.warn('mermaid render failed', fehler)
        }
      }
    }

    void renderAll()

    // Re-scan when the content changes (CollabReadView poll, edit/save
    // round-trip, etc.). MutationObserver fires on any DOM mutation in
    // the root subtree — debounced to a microtask.
    let scheduled = false
    const observer = new MutationObserver(() => {
      if (scheduled) return
      scheduled = true
      queueMicrotask(() => {
        scheduled = false
        void renderAll()
      })
    })
    observer.observe(root, { childList: true, subtree: true })

    return () => {
      cancelled = true
      observer.disconnect()
    }
  }, [root])
}
